'use client'

import { useEffect, useState } from 'react'
import { useSession } from '@wiggot/auth/hooks/useSession'

import Button from '@wiggui/ds/Button'
import GoogleIcon from '@wiggui/ds/GoogleIcon'
import TextArea from '@wiggui/ds/TextArea'
import Modal from '@wiggui/ds/Modal'
import { toast } from '@wiggui/ds/Toast'
import { postSendUpdatePlanMessage } from '../../services'
import { useAmplitude } from '@wiggot/analytics/src/providers/amplitud-provider/AmplitudeProvider'
import { composeClasses } from '@wiggot/utils/src/strings'

interface IProps {
  isOpen: boolean
  onClose: () => void
  defaultValue?: '1' | '2' | '3' | '4'
}
const CHECKBOXS = [
  {
    id: '1',
    label: 'Cambiar plan',
    isChecked: false,
    icon: 'change_circle'
  },
  {
    id: '2',
    label: 'Renovar plan',
    isChecked: false,
    icon: 'autorenew'
  },
  {
    id: '3',
    label: 'Adquirir paquetes de anuncios en i24 y Mercado Libre',
    isChecked: false,
    icon: 'shopping_cart'
  },
  {
    id: '4',
    label: 'Reportar un error',
    isChecked: false,
    icon: 'report'
  }
]
export const ModalHelp = ({ isOpen, onClose, defaultValue }: IProps) => {
  const { trackEvent } = useAmplitude()
  const { profile } = useSession()
  const [checkboxs, setCheckboxs] = useState(CHECKBOXS)
  const [textArea, setTextArea] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const subject = checkboxs.find((checkbox) => checkbox.isChecked)?.label

  useEffect(() => {
    if (defaultValue) {
      const newCheckboxs = checkboxs.map((checkbox) => {
        if (checkbox.id === defaultValue) {
          return {
            ...checkbox,
            isChecked: !checkbox.isChecked
          }
        }
        return {
          ...checkbox,
          isChecked: false
        }
      })
      setCheckboxs(newCheckboxs)
    }
  }, [])

  const handleOnChange = (label: string) => {
    const newCheckboxs = checkboxs.map((checkbox) => {
      if (checkbox.label === label) {
        return {
          ...checkbox,
          isChecked: !checkbox.isChecked
        }
      }
      return {
        ...checkbox,
        isChecked: false
      }
    })
    setCheckboxs(newCheckboxs)
  }
  const sendAmplitudeEvent = () => {
    if (!subject) {
      return
    }
    const subjectToTypeMap = {
      [CHECKBOXS[0].label]: 'cambiar',
      [CHECKBOXS[1].label]: 'renovar',
      [CHECKBOXS[2].label]: 'paquete',
      [CHECKBOXS[3].label]: 'error'
    }

    const eventType = subjectToTypeMap[subject]

    if (eventType) {
      trackEvent({
        name: 'storeContactModal_action',
        eventProperties: {
          type: eventType
        }
      })
    }
  }
  const onSendHelp = async () => {
    const subject = checkboxs.find((checkbox) => checkbox.isChecked)?.label
    try {
      setIsLoading(true)
      const response = await postSendUpdatePlanMessage({
        userId: profile?.userId ?? 0,
        comments: textArea,
        subject
      })
      if (response.isSuccess) {
        toast({
          type: 'success',
          title: 'Tu petición fue enviada, en breve te contactaremos'
        })
        sendAmplitudeEvent()
        handleClose()
        return
      }
      toast({
        type: 'error',
        title: 'No se envió tu petición'
      })
    } catch (error) {
      toast({
        type: 'error',
        title: 'No se envió tu petición'
      })
    } finally {
      setIsLoading(false)
    }
  }
  const handleClose = () => {
    setCheckboxs(CHECKBOXS)
    setTextArea('')
    onClose()
  }
  return (
    <Modal
      closeOnEscape
      closeOnOutsideClick
      enableBgBlur
      className="w-[620px]"
      isOpen={isOpen}
      onClose={onClose}
    >
      <Modal.Header className="w-full flex justify-between items-center pt-3 px-6 pb-1 border-b border-surface-strokes">
        <h3 className="text-lg font-semibold">¿Cómo te podemos ayudar?</h3>
        <button
          type="button"
          onClick={onClose}
          className="w-12 h-12 flex justify-center items-center p-2"
        >
          <GoogleIcon name="close" />
        </button>
      </Modal.Header>
      <Modal.Body className="w-full p-6 flex flex-col gap-6">
        {checkboxs.map((checkbox) => (
          <button
            type="button"
            key={checkbox.label}
            onClick={() => handleOnChange(checkbox?.label)}
            className={composeClasses(
              ' hover:bg-button-disabled flex justify-between w-full  active:border-2 active:border-primary  hover:border-primary items-center p-4 rounded-xl',
              checkbox.isChecked
                ? 'border-2 border-primary bg-button-disabled'
                : 'border border-surface-strokes bg-white'
            )}
          >
            <div
              className={composeClasses(
                'flex gap-1 whitespace-nowrap text-[15px]',
                checkbox.isChecked && 'font-semibold'
              )}
            >
              <GoogleIcon name={checkbox.icon} />
              {checkbox.label}
            </div>
            {checkbox.isChecked ? (
              <GoogleIcon name="check_circle" />
            ) : (
              <div className="rounded-full h-[20px] w-[20px] bg-white border-2 border-primary" />
            )}
          </button>
        ))}
        <TextArea
          placeholder="Agrega tus comentarios"
          classNameLabel="!text-secondary text-xs"
          className="w-full"
          setValue={setTextArea}
          value={textArea}
          maxCharacters={200}
        />
      </Modal.Body>
      <Modal.Footer className="border-t border-surface-strokes w-full py-3 px-6 flex justify-between items-center">
        <Button variant="link" onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          isLoading={isLoading}
          isDisabled={!subject}
          onClick={onSendHelp}
          variant="secondary"
        >
          Solicitar ayuda
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
